import { ApiError } from "./api.js";

/**
 * User-facing error copy. Students should never see raw error text, HTTP codes,
 * or internal error keys — only plain, reassuring language. Raw detail is logged
 * to the console (and the server logs its own side) for debugging.
 *
 * Add a new entry here and map to it in friendlyError(); never inline a message
 * at a call site.
 */
export const ERROR_MESSAGES = {
  generic: "Something went wrong. Please try again in a moment.",
  network: "We couldn't reach the server. Check your connection and try again.",
  session_expired: "Your session expired. Please sign in again.",
  not_found: "We couldn't find that. It may have moved or been removed.",
  server: "Something went wrong on our end. We're on it. Please try again shortly.",
  rate_limited: "You're going a bit fast. Take a short break and try again.",
  not_signed_in: "Please sign in to continue.",
  no_forms: "No practice tests are available right now. Please check back soon.",
  load_failed: "We couldn't load this right now. Please try again.",
} as const;

export type ErrorMessageKey = keyof typeof ERROR_MESSAGES;

// Map specific server error codes (the `error` field in API responses) to copy.
// Codes not listed fall through to the status-based default.
const CODE_TO_KEY: Record<string, ErrorMessageKey> = {
  no_session: "not_signed_in",
  invalid_session: "session_expired",
  missing_authorization: "not_signed_in",
  no_forms_available: "no_forms",
  rate_limited: "rate_limited",
  session_not_found: "not_found",
  session_not_found_or_not_submitted: "not_found",
  form_not_found: "load_failed",
  form_snapshot_missing: "load_failed",
};

/**
 * Turn any caught error into safe, friendly user-facing text. Logs the raw
 * error to the console for debugging; the returned string never contains codes,
 * stacks, or server detail. In dev builds the raw detail is appended so we can
 * see it while developing.
 */
export function friendlyError(e: unknown, fallback: ErrorMessageKey = "generic"): string {
  // Always log the real thing for debugging (never shown to the user).
  console.error("[error]", e);

  let key: ErrorMessageKey = fallback;
  if (e instanceof ApiError) {
    const mapped = e.code ? CODE_TO_KEY[e.code] : undefined;
    if (mapped) {
      key = mapped;
    } else if (e.status === 401) {
      key = "session_expired";
    } else if (e.status === 404) {
      key = "not_found";
    } else if (e.status === 429) {
      key = "rate_limited";
    } else if (e.status >= 500) {
      key = "server";
    } else if (e.status === 0) {
      key = "network";
    }
  } else if (e instanceof TypeError) {
    // fetch() throws TypeError on network failure.
    key = "network";
  } else if (e instanceof DOMException && e.name === "AbortError") {
    // Our 15s request timeout aborts the fetch; treat it as a connection issue.
    key = "network";
  }

  const msg = ERROR_MESSAGES[key];
  if (import.meta.env.DEV) {
    const raw = e instanceof Error ? e.message : String(e);
    return `${msg}  [dev: ${raw}]`;
  }
  return msg;
}
